import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../config/firebase";
import { getStaffProvisioningEnvironment } from "../config/staffProvisioningEnvironment";
import "./styles/DeviceDiagnosticsControls.css";

const record = value => value !== null && typeof value === "object" && !Array.isArray(value);
const deviceId = value => typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
const version = value => value === null || (typeof value === "string" && /^[A-Za-z0-9][A-Za-z0-9.+_-]{0,31}$/.test(value));
const timestamp = value => Number.isSafeInteger(value) && value > 0 && value <= 8640000000000000;
const cursorValid = (value, restaurantId, projectId) => record(value) && Object.keys(value).length === 4 &&
	value.version === 1 && value.restaurantId === restaurantId && value.firebaseProject === projectId && deviceId(value.afterDeviceId);

export function readPage(value, restaurantId, projectId, cursor) {
	const invalid = () => { throw new Error("Invalid diagnostics response"); };
	if (!record(value) || value.success !== true || value.observationOnly !== true || value.restaurantId !== restaurantId ||
		value.firebaseProject !== projectId || !timestamp(value.observedAt) || value.staleAfterMs !== 900000 ||
		!Array.isArray(value.devices) || value.devices.length > 50 || typeof value.hasMore !== "boolean") invalid();
	let previous = cursor?.afterDeviceId || "";
	const devices = value.devices.map(row => {
		if (!record(row) || !deviceId(row.deviceId) || row.deviceId <= previous || typeof row.label !== "string" ||
			!row.label.trim() || row.label.length > 80 || !["enrolled", "revoked", "expired", "unknown"].includes(row.enrollmentStatus) ||
			!["recent", "stale", "unknown"].includes(row.telemetryStatus)) invalid();
		previous = row.deviceId;
		let observation = null;
		if (row.telemetryStatus === "unknown") {
			if (row.receivedAt !== null || row.observation !== null) invalid();
		} else {
			const item = row.observation;
			if (!timestamp(row.receivedAt) || row.receivedAt > value.observedAt ||
				row.telemetryStatus !== (value.observedAt - row.receivedAt >= value.staleAfterMs ? "stale" : "recent") ||
				!record(item) || !["android", "ios"].includes(item.platform) || !version(item.appVersion) || !version(item.buildVersion) ||
				!["active", "background", "inactive", "unknown"].includes(item.appState) ||
				!["locked", "unlocked", "unknown"].includes(item.sessionLock) ||
				!(item.printerModels === null || (Array.isArray(item.printerModels) && item.printerModels.length <= 2 &&
					new Set(item.printerModels).size === item.printerModels.length && item.printerModels.every(model => ["TSP100IV", "V-WRP2"].includes(model))))) invalid();
			observation = { platform: item.platform, appVersion: item.appVersion, buildVersion: item.buildVersion,
				appState: item.appState, sessionLock: item.sessionLock, printerModels: item.printerModels };
		}
		return { deviceId: row.deviceId, label: row.label, enrollmentStatus: row.enrollmentStatus,
			telemetryStatus: row.telemetryStatus, receivedAt: row.receivedAt, observation };
	});
	if (value.hasMore ? !devices.length || !cursorValid(value.nextCursor, restaurantId, projectId) ||
		value.nextCursor.afterDeviceId !== previous : value.nextCursor !== null) invalid();
	return { devices, nextCursor: value.nextCursor, observedAt: value.observedAt };
}

export default function DeviceDiagnosticsControls({ restaurantId }) {
	const [user, loading, error] = useAuthState(auth);
	const actor = useRef({ user: null, revision: 0 });
	if (actor.current.user !== user) actor.current = { user, revision: actor.current.revision + 1 };
	const environment = getStaffProvisioningEnvironment();
	const projectId = functions.app.options.projectId;
	if (!restaurantId || !environment) return null;
	if (loading) return <p role="status">Checking support access...</p>;
	if (error || !user || auth.currentUser !== user) return <p role="alert">Sign in with an authorized support account.</p>;
	return <ScopedDiagnostics key={JSON.stringify([restaurantId, environment, projectId, user.uid, actor.current.revision])}
		restaurantId={restaurantId} environment={environment} projectId={projectId} user={user} instance={functions} />;
}

function ScopedDiagnostics({ restaurantId, environment, projectId, user, instance }) {
	const [devices, setDevices] = useState([]);
	const [nextCursor, setNextCursor] = useState(null);
	const [observedAt, setObservedAt] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const lifecycle = useRef(0), alive = useRef(false), request = useRef(0), paging = useRef(false);
	const isCurrent = useCallback(generation => alive.current && lifecycle.current === generation && auth.currentUser === user &&
		instance === functions && functions.app.options.projectId === projectId && getStaffProvisioningEnvironment() === environment,
		[user, instance, projectId, environment]);
	const refresh = useCallback(async (cursor = null) => {
		const generation = lifecycle.current;
		if (!isCurrent(generation) || (cursor && paging.current)) return;
		const currentRequest = ++request.current;
		paging.current = true; setLoading(true); setError("");
		if (!cursor) { setDevices([]); setNextCursor(null); setObservedAt(null); }
		try {
			const response = await httpsCallable(instance, "getScervStaffDeviceDiagnostics")({ firebaseProject: projectId, restaurantId, ...(cursor ? { cursor } : {}) });
			if (!isCurrent(generation) || currentRequest !== request.current) return;
			const page = readPage(response.data, restaurantId, projectId, cursor);
			setDevices(previous => cursor ? [...previous, ...page.devices] : page.devices);
			setNextCursor(page.nextCursor); setObservedAt(page.observedAt);
		} catch {
			if (isCurrent(generation) && currentRequest === request.current) setError("Device diagnostics are unavailable. Access or telemetry could not be confirmed.");
		} finally {
			if (isCurrent(generation) && currentRequest === request.current) { paging.current = false; setLoading(false); }
		}
	}, [isCurrent, instance, projectId, restaurantId]);
	useEffect(() => {
		const generation = ++lifecycle.current; alive.current = true; refresh();
		return () => { alive.current = false; lifecycle.current = generation + 1; paging.current = false; };
	}, [refresh]);
	const status = row => row.receivedAt && observedAt - row.receivedAt >= 900000 ? "Stale telemetry" :
		row.telemetryStatus === "recent" ? "Recent telemetry" : row.telemetryStatus === "stale" ? "Stale telemetry" : "Unknown / no telemetry";
	return <section className="device-diagnostics" aria-label="Device diagnostics">
		<header><h2>Device diagnostics</h2><button type="button" onClick={() => refresh()}>Refresh</button></header>
		<p className="device-diagnostics-meta">{environment} environment{observedAt ? ` | Snapshot ${new Date(observedAt).toLocaleString()}` : ""}</p>
		<p className="device-diagnostics-notice">Device-reported observations only. A heartbeat does not prove printer connectivity, drawer operation, or payment success. Stale means no accepted heartbeat for at least 15 minutes; unknown means no usable telemetry.</p>
		{error && <p role="alert">{error}</p>}
		{loading && <p role="status">Loading device diagnostics...</p>}
		{observedAt && !devices.length && <p>No enrolled devices found.</p>}
		{devices.length > 0 && <div className="device-diagnostics-scroll"><table>
			<thead><tr><th>Device</th><th>Enrollment</th><th>Heartbeat</th><th>App / build</th><th>Last app state / lock</th><th>Reported runtime models</th></tr></thead>
			<tbody>{devices.map(row => <tr key={row.deviceId}>
				<td>{row.label}<small title={row.deviceId}>{row.deviceId.slice(0, 12)}</small></td>
				<td>{row.enrollmentStatus}</td>
				<td><strong>{status(row)}</strong><small>{row.receivedAt ? new Date(row.receivedAt).toLocaleString() : "No usable heartbeat"}</small></td>
				<td>{row.observation ? <>{row.observation.platform}<small>{row.observation.appVersion || "Unknown version"} / {row.observation.buildVersion || "Unknown build"}</small></> : "Unknown"}</td>
				<td>{row.observation ? `${row.observation.appState} / ${row.observation.sessionLock}` : "Unknown"}</td>
				<td>{row.observation?.printerModels === null || !row.observation ? "Unknown" : row.observation.printerModels.length ? row.observation.printerModels.join(", ") : "None reported"}</td>
			</tr>)}</tbody>
		</table></div>}
		{nextCursor && <button type="button" disabled={loading} onClick={() => refresh(nextCursor)}>Load more devices</button>}
	</section>;
}
